import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import blog from "@/data/blog.json";
import { APP_URL } from "@/data/landing";
import { BlogLayout, useSeo, formatDate } from "@/components/blog/BlogLayout";
import NotFound from "@/pages/NotFound";

const POSTS = blog.posts;
const SITE = "https://anxiety-fix.com";

const Block = ({ block }) => {
  if (block.type === "h2") return <h2 className="font-heading text-2xl sm:text-3xl mt-10 mb-3 text-afwn-navy">{block.text}</h2>;
  if (block.type === "ul") return <ul className="my-4 space-y-2 list-disc pl-6 text-afwn-slate leading-relaxed text-lg">{block.items.map((it) => <li key={it}>{it}</li>)}</ul>;
  return <p className="my-4 text-afwn-slate leading-relaxed text-lg">{block.text}</p>;
};

const PostCard = ({ post }) => (
  <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group block bg-white rounded-3xl border border-afwn-navy/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden h-full">
    <div className="h-2" style={{ background: post.color }} />
    <div className="p-7 flex flex-col h-full">
      <span className="inline-block self-start px-3 py-1 text-xs tracking-wider uppercase rounded-full font-semibold text-white" style={{ background: post.color }}>{post.categoryLabel}</span>
      <h2 className="mt-4 font-heading text-2xl leading-snug text-afwn-navy group-hover:text-afwn-green transition-colors">{post.title}</h2>
      <p className="mt-3 text-afwn-slate text-sm leading-relaxed flex-1">{post.description}</p>
      <div className="mt-5 flex items-center justify-between text-xs text-afwn-slate">
        <span>{formatDate(post.date)}</span>
        <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readingMinutes} min read</span>
      </div>
    </div>
  </Link>
);

export function BlogIndex() {
  useSeo({
    title: "Wellness Blog | Anxiety Fix Wellness Network",
    description: "Educational articles and practical self-help tools for Anxiety, PTSD, and Postpartum wellness from the Anxiety Fix Wellness Network.",
    path: "/blog",
  });
  return (
    <BlogLayout backTo="/" backLabel="Back to home">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="blog-index">
        <p className="text-sm tracking-wider uppercase font-semibold text-afwn-green">Wellness Blog</p>
        <h1 className="mt-3 font-heading font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl">Small steps toward calm</h1>
        <p className="mt-4 text-lg text-afwn-slate max-w-2xl">Educational articles and practical self-help tools for the anxious, the healing-at-their-own-pace, and the newly-postpartum. Written with care; never a substitute for professional support.</p>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {POSTS.map((p) => <PostCard key={p.slug} post={p} />)}
        </div>
      </main>
    </BlogLayout>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  const jsonLd = useMemo(() => post && {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "Anxiety Fix Wellness Network", url: SITE },
    publisher: { "@type": "Organization", name: "Anxiety Fix Wellness Network", url: SITE },
    mainEntityOfPage: `${SITE}/blog/${post.slug}`,
    image: `${SITE}/og-image.jpg`,
  }, [post]);
  useSeo({
    title: post ? `${post.title} | Anxiety Fix Wellness Network` : "Article not found",
    description: post?.description || "",
    path: `/blog/${slug}`,
    jsonLd,
  });
  if (!post) return <NotFound />;
  const others = POSTS.filter((p) => p.slug !== post.slug);
  return (
    <BlogLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="blog-post">
        <span className="inline-block px-3 py-1 text-xs tracking-wider uppercase rounded-full font-semibold text-white" style={{ background: post.color }}>{post.categoryLabel}</span>
        <h1 className="mt-5 font-heading font-light tracking-tight text-4xl sm:text-5xl leading-[1.1]" data-testid="blog-post-title">{post.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-afwn-slate">
          <span>Anxiety Fix Wellness Network</span><span aria-hidden>·</span>
          <time dateTime={post.date}>{formatDate(post.date)}</time><span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {post.readingMinutes} min read</span>
        </div>
        <p className="mt-10 text-xl text-afwn-navy/90 leading-relaxed font-light">{post.intro}</p>
        {post.blocks.map((b, i) => <Block key={`${b.type}-${i}`} block={b} />)}

        <div className="mt-14 rounded-3xl p-8 text-white afwn-grain relative overflow-hidden" style={{ background: post.color }} data-testid="blog-post-cta">
          <h3 className="font-heading text-3xl">Want support that's there at 3 a.m. too?</h3>
          <p className="mt-3 text-white/85">Try the {post.categoryLabel} AI Assistant and the full 52-week wellness program free for 7 days.</p>
          <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full font-medium px-7 py-3.5 bg-white text-afwn-navy hover:scale-[1.03] transition-transform duration-300">Start your free trial <ArrowRight size={16} /></a>
        </div>

        <p className="mt-10 text-xs text-afwn-slate leading-relaxed" data-testid="blog-post-disclaimer">This article is for educational and wellness purposes only. Anxiety Fix Wellness Network is not a medical or mental-health treatment provider. Nothing here diagnoses, treats, cures, or prevents any condition or replaces care from a qualified professional. If you are in crisis, call or text 988 (US) or contact local emergency services.</p>

        <div className="mt-16 pt-10 border-t border-afwn-navy/10">
          <p className="text-sm tracking-wider uppercase font-semibold text-afwn-green">Keep reading</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {others.map((p) => <PostCard key={p.slug} post={p} />)}
          </div>
        </div>
      </article>
    </BlogLayout>
  );
}
